import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useToast } from "../../../hooks/use-toast";
import {
    ChevronLeft,
    ChevronRight,
    FileTerminal,
    Folder,
    Globe,
    Layers,
    List,
    Search,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Skeleton } from "../interfaces/CommonInterfaces";
import { myLocalStorage } from "../../StorageHelper";
import { fetchCategories, getCategoryItems, updateCategory } from "./Api/policyprofileapi";
import { AppResource } from "./interface";
import { fetchUrlControlDataWithProfile, getFoldersForProfile } from "../Resources/Api/resourceApi";
import { getProfileResourceNameField } from "./helper";
import useUserStore from "../../../services/userStore";
import { getCategoryItemsWithoutProfileId } from "../ResourceCategory/Api/resourcecategoryapi";
import PolicyProfileHeader from "./PolicyProfileHeader";
import "../ResourceCategory/ResourceCategories.css";

const RESOURCE_TYPES = ["app", "folder", "url"] as const;

export default function PolicyProfilesCategories() {
    const location = useLocation();
    const { id, osType: osTypeParam } = useParams();
    const [selectedResourceType, setSelectedResourceType] = useState<"app" | "folder" | "url">("app");
    const [selectedCategory, setSelectedCategory] = useState<AppResource | null>(null);
    const [categoriesSearch, setCategoriesSearch] = useState("");
    const [itemsSearch, setItemsSearch] = useState("");
    const [currentCategoriesPage, setCurrentCategoriesPage] = useState(1);
    const [currentItemsPage, setCurrentItemsPage] = useState(1);
    const { toast } = useToast();
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const [data, setData] = useState<AppResource[]>([]);
    const profileId = id ? Number(id) : -1;
    const resourceNameField = getProfileResourceNameField(selectedResourceType);
    const [matchedResources, setMatchedResources] = useState<any[]>([]);
    const [categoryTrustLevels, setCategoryTrustLevels] = useState<Record<string, string>>({});
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [itemsLoading, setItemsLoading] = useState(false);
    const { email } = useUserStore((state) => state.user);

    const trustLevelOptions = [
        { value: "not_configured", label: "NOT CONFIGURED" },
        { value: "ENABLED", label: "ALLOWED" },
        { value: "DISABLED", label: "DENIED" },
        { value: "DYNAMIC", label: "TIMED" }
    ];

    const CATEGORIES_PER_PAGE = 6;
    const ITEMS_PER_PAGE = 10;

    const profileName = location.state?.profileName || localStorage.getItem("currentProfileName") || "Unknown Profile";
    const osType = (osTypeParam as "windows" | "linux" | "mac") || "windows";

    const getCategoryTrustKey = (categoryName: string, resourceType?: string) =>
        `${resourceType}:${categoryName}`;

    const handleTrustLevelChange = async (categoryName: string, profileName: string, trustLevel: string, category: AppResource) => {
        const trustKey = getCategoryTrustKey(categoryName, category.resourceType);
        const previousTrustLevel = getCategoryTrustLevel(category);
        const profileExists = category.profiles.some(p => p.profileId === profileId);
        const updatedProfiles = profileExists
            ? category.profiles.map(p => ({
                profileId: p.profileId,
                name: p.name,
                trustLevel: p.profileId === profileId ? trustLevel : p.trustLevel,
            }))
            : [
                ...category.profiles,
                {
                    profileId,
                    name: profileName,
                    trustLevel,
                },
            ];

        setCategoryTrustLevels(prev => ({
            ...prev,
            [trustKey]: trustLevel,
        }));

        let osType = (category.operationSystem.toUpperCase() === "MAC" || category.operationSystem.toUpperCase() === "MACOS") ? "MACOS" : category.operationSystem.toUpperCase();


        const payload = {
            reputationScore: category.reputationScore,
            oldName: category.name,
            newName: category.name,
            oldSearchPatterns: category.searchPatterns,
            os: osType,
            resourceType: category.resourceType,
            profiles: updatedProfiles,
            userEmail: email,
            tenantName: category.tenantName,
        };

        toast({
            title: "Trust level updating",
            description: `Trust level for "${categoryName}" → "${profileName}" updating to ${trustLevelOptions.find(opt => opt.value === trustLevel)?.label ?? trustLevel
                }`,
        });

        try {
            const response = await updateCategory(payload);
            if (response) {
                fetchData(false);
            }
            toast({
                title: "Trust level updated",
                description: `Trust level for "${categoryName}" → "${profileName}" set to ${trustLevelOptions.find(opt => opt.value === trustLevel)?.label ?? trustLevel
                    }`,
            });
        } catch (error: any) {
            setCategoryTrustLevels(prev => ({
                ...prev,
                [trustKey]: previousTrustLevel,
            }));
            toast({
                title: "Trust level update failed",
                description: typeof error === "string"
                    ? error
                    : error?.message || `Unable to update trust level for "${categoryName}".`,
                variant: "destructive",
            });
        }

    };

    const getResourceTypeIcon = (resourceType: string) => {
        switch (resourceType) {
            case "app": return <FileTerminal className="w-4 h-4" />;
            case "folder": return <Folder className="w-4 h-4" />;
            case "url": return <Globe className="w-4 h-4" />;
            default: return <FileTerminal className="w-4 h-4" />;
        }
    };

    const getResourceTypeName = (resourceType: string) => {
        switch (resourceType) {
            case "app": return "Applications";
            case "folder": return "Folders";
            case "url": return "URLs";
            default: return "Resources";
        }
    };

    const getCategoryTrustLevel = (category: AppResource | null) => {
        if (!category) return "not_configured";
        return categoryTrustLevels[getCategoryTrustKey(category.name, category.resourceType)] ??
            category.profiles?.find(p => p.name === profileName)?.trustLevel ?? "not_configured";
    };

    const isNotConfiguredTrustLevel = (trustLevel?: string) => {
        return trustLevel === "not_configured";
    };

    const fetchData = async (isFirst = true) => {
        setCategoriesLoading(isFirst);
        const response: AppResource[] = await fetchCategories(osType, tenantName, selectedResourceType);



        setData(response.filter(cat => cat.operationSystem.toLowerCase() === (osType === "mac" ? "macos" : osType).toLowerCase()));
        setCategoriesLoading(false);
    };

    const fetchCategoryItems = async (category: AppResource | null) => {
        if (!category) return;

        const trustLevel = getCategoryTrustLevel(category);


        setItemsLoading(true);

        let response = [];
        if (selectedResourceType === "app" && isNotConfiguredTrustLevel(trustLevel)) {
            response = await getCategoryItemsWithoutProfileId(
                osType,
                tenantName,
                selectedResourceType,
                category.name
            );
        } else {
            response = await getCategoryItems(
                osType,
                tenantName,
                selectedResourceType,
                category.name,
                profileId
            );
        }



        const manualExclusions = Array.isArray(category.manualExclusions) ? category.manualExclusions : [];
        const exclusionNames = manualExclusions.map(e => e.name);

        let filteredResponse: any[] = [];

        if (selectedResourceType === "app") {
            const normalizedResponse = Array.isArray(response)
                ? response.map((entry: any) => {
                    if (entry?.defaultProfileAppsResource) return entry;

                    return {
                        defaultProfileAppsResource: {
                            profileId,
                            appId: entry?.id,
                            profileName: decodeURIComponent(profileName),
                            appName: entry?.name,
                            appPath: entry?.path,
                            size: entry?.size,
                            fingerprintSha256: entry?.fingerprintSha256,
                            signature: entry?.signature,
                            type: entry?.type || "ORIGINAL",
                            profileResource: "INHERITED",
                            trustLevel: trustLevel === "not_configured" ? "DISABLED" : trustLevel,
                            categoryName: entry?.categories?.[0]?.categoryName || category.name,
                            reputationScore: entry?.reputationScore,
                        },
                        applicationReputation: null,
                    };
                })
                : [];

            const seen = new Set<string>();
            filteredResponse = normalizedResponse.filter((entry: any) => {
                const appPath = entry?.defaultProfileAppsResource?.appPath;
                if (!appPath || exclusionNames.includes(appPath) || seen.has(appPath)) return false;
                seen.add(appPath);
                return true;
            });
        } else {
            let profileResourceList: any[] = [];
            if (selectedResourceType === "folder") {
                const folderResponse = await getFoldersForProfile(profileId, osType);
                profileResourceList = folderResponse.sort((a: any, b: any) => a.folderId - b.folderId);
            } else {
                profileResourceList = await fetchUrlControlDataWithProfile(profileId, osType);
            }

            const filteredResources = response.content.filter((resource: any) =>
                resource.categories?.some((c: any) => c.categoryName.toLowerCase() === category.name.toLowerCase())
            );

            const seen = new Set<string>();
            filteredResponse = profileResourceList.filter((profileResource: any) => {
                const match = filteredResources.some((categoryResource: any) =>
                    profileResource[resourceNameField] === categoryResource.path ||
                    profileResource[resourceNameField] === categoryResource.name
                );
                const key = profileResource[resourceNameField];
                if (!match || seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        }

        setMatchedResources(filteredResponse);
        setItemsLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [osType, tenantName, selectedResourceType]);

    useEffect(() => {
        fetchCategoryItems(selectedCategory);
    }, [selectedCategory, categoryTrustLevels, selectedResourceType]);

    const filteredCategories = useMemo(() => {
        return data.filter(cat => cat.name.toLowerCase().includes(categoriesSearch.toLowerCase()));
    }, [categoriesSearch, data]);

    const filteredItems = useMemo(() => {
        if (!selectedCategory) return [];

        return matchedResources.filter((item) => {

            let displayValue = "";
            switch (selectedResourceType) {
                case "app":
                    displayValue = item?.defaultProfileAppsResource?.appPath ?? "";
                    break;
                case "folder":
                    displayValue = item?.path ?? "";
                    break;
                case "url":
                    displayValue = item?.urlName ?? "";
                    break;
                default:
                    displayValue = "";
            }

            const search = itemsSearch.toLowerCase();
            if (search.trim().length > 0) {
                return displayValue?.toLowerCase().includes(search)
            } else return item

        });
    }, [matchedResources, selectedCategory, itemsSearch, selectedResourceType]);


    const totalCategoriesPages = Math.ceil(filteredCategories.length / CATEGORIES_PER_PAGE);
    const paginatedCategories = useMemo(() => {
        const start = (currentCategoriesPage - 1) * CATEGORIES_PER_PAGE;
        return filteredCategories.slice(start, start + CATEGORIES_PER_PAGE);
    }, [filteredCategories, currentCategoriesPage]);

    const totalItemsPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
    const paginatedItems = useMemo(() => {
        const start = (currentItemsPage - 1) * ITEMS_PER_PAGE;
        return filteredItems.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredItems, currentItemsPage]);

    useEffect(() => {
        setCurrentCategoriesPage(1);
    }, [categoriesSearch, selectedResourceType]);

    useEffect(() => {
        setCurrentItemsPage(1);
    }, [itemsSearch, selectedCategory]);

    const renderSkeletonRows = (count: number) => (
        <div className="category-scroll category-rows">
            {Array.from({ length: count }).map((_, index) => (
                <div key={index} className="category-skeleton-row">
                    <Skeleton className="category-skeleton-icon" />
                    <div className="category-skeleton-copy">
                        <Skeleton className="h-3 w-3/4" />
                        <Skeleton className="h-2 w-1/2" />
                    </div>
                </div>
            ))}
        </div>
    );

    const visibleCategories = paginatedCategories.filter((category) => {
        const os = category.operationSystem?.toUpperCase();
        const profileOS = osType?.toUpperCase();

        return (
            os === profileOS ||
            (os === "MACOS" && profileOS === "MAC")
        );
    });

    const handleResourceTypeChange = (value: "app" | "folder" | "url") => {
        setSelectedResourceType(value);
        setSelectedCategory(null);
        setMatchedResources([]);
        setItemsSearch("");
        setCurrentItemsPage(1);
        setCategoriesSearch("");
        setCurrentCategoriesPage(1);
    };

    const getItemDisplayValue = (item: any) => {
        switch (selectedResourceType) {
            case "app": return item?.defaultProfileAppsResource?.appPath ?? "";
            case "folder": return item?.path ?? "";
            case "url": return item?.urlName ?? "";
            default: return "";
        }
    };

    return (
        <div className="endpoint-detail-page policy-detail-page custom-scrollbar flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto bg-background">
            <PolicyProfileHeader
                profileName={decodeURIComponent(profileName)}
                os={osType}
                section="Categories"
            />

            <div className="resource-categories policy-categories-body">
                <div className="category-workspace">
                    <div className="category-types">
                        <Card className="category-panel">
                            <CardHeader className="category-panel-header">
                                <CardTitle className="category-panel-title">
                                    <span className="category-icon">
                                        <Layers />
                                    </span>
                                    Types
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="category-types-content">
                                <div className="space-y-1">
                                    {RESOURCE_TYPES.map((type) => (
                                        <Button
                                            key={type}
                                            variant={selectedResourceType === type ? "default" : "ghost"}
                                            className="category-type"
                                            aria-current={selectedResourceType === type ? "true" : undefined}
                                            onClick={() => handleResourceTypeChange(type)}
                                            data-testid={`tab-${type}`}
                                        >
                                            {getResourceTypeIcon(type)}
                                            {getResourceTypeName(type)}
                                        </Button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="category-column">
                        <Card className="category-panel category-list-panel">
                            <CardHeader className="category-panel-header">
                                <div className="category-panel-heading">
                                    <CardTitle className="category-panel-title">
                                        <span className="category-icon">
                                            {getResourceTypeIcon(selectedResourceType)}
                                        </span>
                                        {getResourceTypeName(selectedResourceType)}{" "}
                                        <span className="category-count">{filteredCategories.length}</span>
                                    </CardTitle>
                                    <div className="category-search">
                                        <Search aria-hidden="true" />
                                        <Input
                                            aria-label="Search categories"
                                            placeholder={`Search ${selectedResourceType} categories...`}
                                            value={categoriesSearch}
                                            onChange={e => setCategoriesSearch(e.target.value)}
                                            className="h-8"
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="category-panel-content">
                                {categoriesLoading ? (
                                    renderSkeletonRows(CATEGORIES_PER_PAGE)
                                ) : visibleCategories.length > 0 ? (
                                    <div className="category-scroll category-rows">
                                        {visibleCategories.map((category, idx) => {
                                            const trustLevel = getCategoryTrustLevel(category);
                                            return (
                                                <Card
                                                    key={idx}
                                                    className="category-entry policy-category-entry"
                                                    data-trust={trustLevel}
                                                    aria-current={selectedCategory?.name === category.name ? "true" : undefined}
                                                    onClick={() => {
                                                        setSelectedCategory(category);
                                                        setCurrentItemsPage(1);
                                                        setItemsSearch("");
                                                    }}
                                                >
                                                    <CardContent className="category-entry-content">
                                                        <div className="category-entry-row">
                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                <span className="category-icon">
                                                                    {getResourceTypeIcon(selectedResourceType)}
                                                                </span>
                                                                <h3 className="font-medium text-sm break-words min-w-0">{category.name}</h3>
                                                            </div>
                                                            <div className="policy-trust-field">
                                                                <span className="policy-trust-label">Trust level</span>
                                                                <Select
                                                                    value={trustLevel}
                                                                    onValueChange={(value) => handleTrustLevelChange(category.name, profileName, value, category)}
                                                                >
                                                                    <SelectTrigger
                                                                        className="policy-trust-select"
                                                                        aria-label={`Trust level for ${category.name}`}
                                                                    >
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        {trustLevelOptions
                                                                            .filter((opt) => trustLevel === "not_configured" || opt.value !== "not_configured")
                                                                            .map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="category-empty">
                                        <div className="category-empty-icon">
                                            {getResourceTypeIcon(selectedResourceType)}
                                        </div>
                                        <h3>No categories found</h3>
                                        <p>
                                            {categoriesSearch
                                                ? "No categories match your search."
                                                : `No ${getResourceTypeName(selectedResourceType).toLowerCase()} categories found for this profile.`}
                                        </p>
                                    </div>
                                )}
                                {totalCategoriesPages > 1 && (
                                    <div className="category-pagination">
                                        <p className="text-sm text-muted-foreground">
                                            Page {currentCategoriesPage} of {totalCategoriesPages}
                                        </p>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentCategoriesPage(prev => Math.max(1, prev - 1))}
                                                disabled={currentCategoriesPage === 1}
                                                data-testid="button-categories-prev"
                                            >
                                                <ChevronLeft className="w-4 h-4" />
                                                Previous
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentCategoriesPage(prev => Math.min(totalCategoriesPages, prev + 1))}
                                                disabled={currentCategoriesPage === totalCategoriesPages}
                                                data-testid="button-categories-next"
                                            >
                                                Next
                                                <ChevronRight className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <div className="category-column">
                        <Card className="category-panel category-list-panel">
                            <CardHeader className="category-panel-header">
                                <div className="category-panel-heading">
                                    <CardTitle className="category-panel-title">
                                        <span className="category-icon">
                                            {getResourceTypeIcon(selectedResourceType)}
                                        </span>
                                        Items{" "}
                                        <span className="category-count">{filteredItems.length}</span>
                                    </CardTitle>
                                    <div className="category-search">
                                        <Search aria-hidden="true" />
                                        <Input
                                            aria-label="Search items"
                                            placeholder={`Search ${selectedResourceType} items...`}
                                            value={itemsSearch}
                                            onChange={e => setItemsSearch(e.target.value)}
                                            className="h-8"
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="category-panel-content">
                                {itemsLoading ? (
                                    renderSkeletonRows(ITEMS_PER_PAGE)
                                ) : !selectedCategory ? (
                                    <div className="category-empty">
                                        <span className="category-empty-icon">
                                            <List />
                                        </span>
                                        <h3>No category selected</h3>
                                        <p>Select a category to view its resources.</p>
                                    </div>
                                ) : matchedResources.length > 0 && paginatedItems.length > 0 ? (
                                    <div className="category-scroll category-rows">
                                        {paginatedItems.map((item: any, idx: number) => (
                                            <Card key={idx} className="category-item">
                                                <CardContent className="category-item-content">
                                                    <div className="flex items-center gap-3">
                                                        <div className="category-icon">
                                                            {getResourceTypeIcon(selectedResourceType)}
                                                        </div>
                                                        <div className="text-sm font-mono text-foreground break-all">
                                                            {getItemDisplayValue(item) || "N/A"}
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="category-empty">
                                        <span className="category-empty-icon">
                                            {getResourceTypeIcon(selectedResourceType)}
                                        </span>
                                        <h3>No items found</h3>
                                        <p>
                                            {itemsSearch && matchedResources.length > 0
                                                ? "No resources match your search."
                                                : "No items found in this category."}
                                        </p>
                                    </div>
                                )}

                                {selectedCategory && totalItemsPages > 1 && (
                                    <div className="category-pagination">
                                        <p className="text-sm text-muted-foreground">
                                            Showing {Math.min((currentItemsPage - 1) * ITEMS_PER_PAGE + 1, filteredItems.length)} to {Math.min(currentItemsPage * ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length} items
                                        </p>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentItemsPage(prev => Math.max(1, prev - 1))}
                                                disabled={currentItemsPage === 1}
                                                data-testid="button-items-prev"
                                            >
                                                <ChevronLeft className="w-4 h-4" />
                                                Previous
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setCurrentItemsPage(prev => Math.min(totalItemsPages, prev + 1))}
                                                disabled={currentItemsPage === totalItemsPages}
                                                data-testid="button-items-next"
                                            >
                                                Next
                                                <ChevronRight className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
